import React, { useState } from 'react';
import { DeleteOutlined, EyeOutlined, FolderOpenOutlined, LeftCircleOutlined, PlusOutlined, RightCircleOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Image, Popconfirm, Space, Typography, Upload, theme } from 'antd';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getPreviewSource } from './useComponent';

const { Text } = Typography;

const tileStyle = {
	flex: '0 0 156px',
	width: 156,
};

const ActionTile = ({ icon, title, caption, loading, onClick }) => {
	const content = (
		<Card hoverable size='small' style={{ ...tileStyle, overflow: 'hidden' }} styles={{ body: { padding: 16 } }}>
			<Flex vertical align='center' justify='center' gap={8} style={{ width: '100%', height: 124, minHeight: 0 }}>
				<Button type='primary' shape='circle' size='large' loading={loading} icon={loading ? undefined : icon} />
				<Text strong>{title}</Text>
				<Text type='secondary' style={{ textAlign: 'center', fontSize: 12 }}>{caption}</Text>
			</Flex>
		</Card>
	);

	if (!onClick) {
		return content;
	}

	return (
		<div onClick={onClick} onKeyDown={(event) => event.key === 'Enter' && onClick()} role='button' tabIndex={0} style={{ cursor: 'pointer' }}>
			{content}
		</div>
	);
};

const SortableImageCard = ({ file, viewModel, isActionOpen, onToggleActions }) => {
	const { token } = theme.useToken();
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: file.uid,
	});

	const style = {
		...tileStyle,
		transform: CSS.Translate.toString(transform),
		transition,
		position: 'relative',
		touchAction: 'none',
		zIndex: isDragging ? 2 : 'auto',
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			<Card
				size='small'
				styles={{ body: { display: 'none' } }}
				style={{
					width: '100%',
					overflow: 'hidden',
					cursor: isDragging ? 'grabbing' : 'grab',
					borderColor: token.colorBorderSecondary,
				}}
				cover={
					<div
						style={{ position: 'relative', aspectRatio: '1 / 1', overflow: 'hidden', background: token.colorFillAlter }}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							onToggleActions(file.uid);
						}}
					>
						<Image
							preview={false}
							src={getPreviewSource(file)}
							alt={file.name || 'image'}
							fallback='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='
							style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
						/>

						{isActionOpen ? <Space size={8} style={{ position: 'absolute', top: 8, right: 8 }}>
							<Button
								type='primary'
								shape='circle'
								size='middle'
								icon={<EyeOutlined />}
								onPointerDown={(event) => {
									event.preventDefault();
									event.stopPropagation();
								}}
								onClick={(event) => {
									event.preventDefault();
									event.stopPropagation();
									viewModel.handlePreview(file);
								}}
							/>

							<Popconfirm
								title='Xóa ảnh này?'
								description='Ảnh sẽ bị gỡ khỏi danh sách hiện tại.'
								onConfirm={() => viewModel.handleRemove(file)}
								okText='Xóa'
								cancelText='Hủy'
							>
								<Button
									danger
									shape='circle'
									size='middle'
									icon={<DeleteOutlined />}
									onPointerDown={(event) => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
									}}
								/>
							</Popconfirm>
						</Space> : null}
					</div>
				}
			/>
		</div>
	);
};

const MultiImagePickerDesktop = ({ viewModel }) => {
	const [activeActionUid, setActiveActionUid] = useState(null);
	const sensor = useSensor(PointerSensor, {
		activationConstraint: {
			distance: 10,
		},
	});

	return (
		<Flex vertical gap={12} style={{ width: '100%' }}>
			<div ref={viewModel.panelRef} tabIndex={0} onClick={viewModel.handlePanelClick} onPaste={viewModel.handlePaste}>
				<Card size='small' styles={{ body: { padding: 16 } }}>
					<Flex wrap gap={12} align='flex-start'>
						<DndContext sensors={[sensor]} onDragEnd={viewModel.onDragEnd}>
							<SortableContext items={viewModel.fileList.map((item) => item.uid)} strategy={horizontalListSortingStrategy}>
								{viewModel.fileList.map((file) => (
									<SortableImageCard
										key={file.uid}
										file={file}
										viewModel={viewModel}
										isActionOpen={activeActionUid === file.uid}
										onToggleActions={(uid) => setActiveActionUid((currentUid) => (currentUid === uid ? null : uid))}
									/>
								))}
							</SortableContext>
						</DndContext>

						{viewModel.canAddMore ? (
							<Upload showUploadList={false} multiple accept={viewModel.acceptValue} beforeUpload={viewModel.handleBeforeUpload} disabled={viewModel.loading}>
								<ActionTile icon={<PlusOutlined />} title='Tải ảnh' caption='Chọn file hoặc dán' loading={viewModel.loading} />
							</Upload>
						) : null}

						{viewModel.canAddMore ? <ActionTile icon={<FolderOpenOutlined />} title='Thư viện' caption='Chọn ảnh có sẵn' onClick={viewModel.openLibrary} /> : null}
					</Flex>
				</Card>
			</div>

			{viewModel.previewImage ? (
				<>
					<Image
						wrapperStyle={{ display: 'none' }}
						preview={{
							visible: viewModel.previewOpen,
							onVisibleChange: viewModel.handlePreviewVisibleChange,
							afterOpenChange: (visible) => !visible && viewModel.setPreviewImage(''),
						}}
						src={viewModel.previewImage}
					/>

					{viewModel.previewOpen && viewModel.fileList.length > 1 ? (
						<Flex
							justify='space-between'
							align='center'
							style={{
								position: 'fixed',
								inset: 0,
								padding: '0 48px',
								pointerEvents: 'none',
								zIndex: 100000,
							}}
						>
							<Button type='primary' shape='circle' size='large' icon={<LeftCircleOutlined />} onClick={viewModel.handlePrev} style={{ pointerEvents: 'auto' }} />
							<Button type='primary' shape='circle' size='large' icon={<RightCircleOutlined />} onClick={viewModel.handleNext} style={{ pointerEvents: 'auto' }} />
						</Flex>
					) : null}
				</>
			) : null}
		</Flex>
	);
};

export default MultiImagePickerDesktop;